import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { matAddSharp } from '@ng-icons/material-icons/sharp';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DataViewModule } from 'primeng/dataview';
import { SelectModule } from 'primeng/select';
import { ScrollTopModule } from 'primeng/scrolltop';
import { SkeletonModule } from 'primeng/skeleton';
import {
  BehaviorSubject,
  combineLatest,
  map,
  Observable,
  of,
  startWith,
  tap,
} from 'rxjs';
import { ApiClientService } from '../../../../services/apiclient/api-client.service';
import { Rabbit } from '../../../../services/apiclient/Rabbit';
import { WeightEntry } from '../../../../services/apiclient/WeightEntry';
import { WeightEntryCardComponent } from '../weight-entry-card/weight-entry-card.component';
import { gameSightDisabled } from '@ng-icons/game-icons';

@Component({
  selector: 'app-weight-entries-overview',
  imports: [
    ButtonModule,
    CommonModule,
    WeightEntryCardComponent,
    DataViewModule,
    SkeletonModule,
    SelectModule,
    ReactiveFormsModule,
    RouterLink,
    ScrollTopModule,
    NgIconComponent,
  ],
  providers: [provideIcons({
    matAddSharp,
    gameSightDisabled
  })],
  templateUrl: './weight-entries-overview.component.html',
  styleUrl: './weight-entries-overview.component.css',
})
export class WeightEntryOverviewComponent {
  private readonly apiService = inject(ApiClientService);
  private readonly messageService = inject(MessageService);

  protected readonly rowsPerPageDefaultOption = 25;
  protected readonly rowsPerPageOptions = [10, 25, 50, 100];
  protected readonly currentPageReportTemplate = '{currentPage} / {totalPages}';

  protected readonly weightEntries$: Observable<WeightEntry[]>;
  protected readonly rabbits$: Observable<Rabbit[]>;
  protected readonly filteredWeightEntries$: Observable<WeightEntry[]>;

  protected readonly selectedRabbit = new FormControl<Rabbit | null>(null);

  protected readonly skeletonsIterationSource = Array(10).fill(0);

  protected readonly deletedEntries$ = new BehaviorSubject(<string[]>[]);

  constructor() {
    this.weightEntries$ = this.apiService.getPreparedWeightEntries();

    this.filteredWeightEntries$ = combineLatest([
      this.weightEntries$,
      this.selectedRabbit.valueChanges.pipe(startWith(null)),
      this.deletedEntries$,
    ]).pipe(
      map(([weightEntries, rabbits, deletedEntries]) => {
        const nonDeletedEntries =
          deletedEntries.length === 0
            ? weightEntries
            : weightEntries.filter((x) => !deletedEntries.includes(x.id));
        return rabbits === null
          ? nonDeletedEntries
          : nonDeletedEntries.filter((entry) => entry.rabbitId === rabbits!.id);
      }),
    );

    this.rabbits$ = this.apiService.getRabbits()
      .pipe(
        startWith([]),
        map(rabbits => this.getRabbitsSortedByDisabledThenByName(rabbits))
      );
  }

  private getRabbitsSortedByDisabledThenByName(rabbits: Rabbit[]): Rabbit[] {
    return rabbits.sort((a, b) => {
      const sortingByDisabledResult = a.isDisabled === b.isDisabled ? 0 : a.isDisabled ? 1 : -1;
      if (sortingByDisabledResult !== 0) {
        return sortingByDisabledResult;
      }
      return a.name.localeCompare(b.name);
    });
  }

  onEntryDeleted(weightEntry: WeightEntry): void {
    this.deletedEntries$.next([...this.deletedEntries$.value, weightEntry.id]);
    this.messageService.add({
      severity: 'success',
      summary: 'Eintrag gelöscht',
      detail: 'Der Eintrag wurde erfolgreich gelöscht.',
    });
  }
}
